
import type { Id, SharedVoice, Voice, VoiceCatalogEntry, VoiceCatalogItem } from '../../types/api';
import { describeVoiceStatus } from '../../hooks';
import { languageLabel } from './format';

export type VoiceGroup = 'mine' | 'pro' | 'std';

export const VOICE_GROUPS: readonly { key: VoiceGroup; label: string; short: string }[] = [
  { key: 'mine', label: 'Мои клоны', short: 'Мои' },
  { key: 'pro', label: 'Профессиональные', short: 'Проф' },
  { key: 'std', label: 'Стандартные', short: 'Станд' },
];

export interface CatalogSource {
  voiceId: string;
  name: string;
  category: string;
  meta: string;
  previewUrl: string | null;
  available: boolean;
  isOwner: boolean;
  profileId: Id | null;
}

export function describeLabels(labels: Record<string, unknown>): string {
  const order = ['accent', 'language', 'description', 'age', 'gender', 'use_case'];
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const key of order) {
    const value = labels[key];
    if (typeof value === 'string' && value.trim() && !seen.has(value)) {
      seen.add(value);
      parts.push(value.trim());
    }
  }
  return parts.join(' · ');
}

export interface TimbreFields {
  accent: string | null;
  gender: string | null;
  age: string | null;
  use_case: string | null;
  description: string | null;
}

export function describeTimbre(entry: TimbreFields): string {
  return [entry.accent, entry.gender, entry.age, entry.use_case, entry.description]
    .filter((part): part is string => Boolean(part && part.trim()))
    .join(' · ');
}

export function catalogKey(voiceId: string): string {
  return `catalog:${voiceId}`;
}

export function speaksButNotNative(voice: SharedVoice, wanted: string | null): boolean {
  if (!wanted || !voice.language) return false;
  return voice.language !== wanted && voice.languages.includes(wanted);
}

export function describeSharedVoice(voice: SharedVoice, wanted: string | null = null): string {
  const native = voice.language ? languageLabel(voice.language) : '';
  const language = speaksButNotNative(voice, wanted)
    ? `${languageLabel(wanted)} · родной ${native.toLowerCase()}`
    : native;
  return [language, describeTimbre(voice)].filter(Boolean).join(' · ');
}

export function fromSharedVoice(voice: SharedVoice, externalVoiceId: string): CatalogSource {
  return {
    voiceId: externalVoiceId,
    name: voice.name,
    category: voice.category,
    meta: describeSharedVoice(voice) || voice.category,
    previewUrl: voice.preview_url,
    available: true,
    isOwner: false,
    profileId: null,
  };
}

export function fromCatalogEntry(entry: VoiceCatalogEntry): CatalogSource {
  const meta = [entry.language ? languageLabel(entry.language) : '', describeTimbre(entry)]
    .filter(Boolean)
    .join(' · ');
  return {
    voiceId: entry.external_voice_id,
    name: entry.name,
    category: entry.category,
    meta: meta || entry.category,
    previewUrl: entry.preview_url,
    available: entry.is_available,
    isOwner: entry.is_owner,
    profileId: entry.voice_profile_id,
  };
}

export function fromLiveItem(item: VoiceCatalogItem): CatalogSource {
  return {
    voiceId: item.voice_id,
    name: item.name,
    category: item.category,
    meta: describeLabels(item.labels) || item.category,
    previewUrl: item.preview_url || null,
    available: true,
    isOwner: item.category === 'cloned',
    profileId: item.voice_profile_id,
  };
}

export interface VoiceOption {
  key: string;
  voiceId: string | null;
  profileId: Id | null;
  name: string;
  group: VoiceGroup;
  meta: string;
  characterName: string | null;
  previewUrl: string | null;
  usable: boolean;
  problem: string | null;
  searchText: string;
}

function groupOf(source: CatalogSource, isOurs: boolean): VoiceGroup {
  if (isOurs || source.isOwner || source.category === 'cloned') return 'mine';
  if (source.category === 'professional') return 'pro';
  return 'std';
}

export interface BuildOptions {
  sources: readonly CatalogSource[] | undefined;
  library: readonly Voice[] | undefined;
  catalogLoaded: boolean;
}

export function buildVoiceOptions({ sources, library, catalogLoaded }: BuildOptions): VoiceOption[] {
  const ours = new Map<string, Voice>();
  for (const voice of library ?? []) {
    if (voice.external_voice_id) ours.set(voice.external_voice_id, voice);
  }

  const options: VoiceOption[] = [];
  const takenProfiles = new Set<Id>();

  for (const source of sources ?? []) {
    const mine = ours.get(source.voiceId);
    if (mine) takenProfiles.add(mine.id);
    const meta = [
      mine ? languageLabel(mine.language) : '',
      source.meta,
      mine ? `персонаж «${mine.character_name}»` : '',
    ]
      .filter(Boolean)
      .join(' · ');
    const problem = !source.available
      ? 'больше не работает'
      : mine && !mine.usable
        ? describeVoiceStatus(mine)
        : null;
    options.push({
      key: catalogKey(source.voiceId),
      voiceId: source.voiceId,
      profileId: mine?.id ?? source.profileId,
      name: mine ? mine.name : source.name,
      group: groupOf(source, Boolean(mine)),
      meta,
      characterName: mine?.character_name ?? null,
      previewUrl: source.previewUrl,
      usable: source.available && (mine ? mine.usable : true),
      problem,
      searchText:
        `${source.name} ${mine?.name ?? ''} ${mine?.character_name ?? ''} ${meta} ${source.category}`.toLowerCase(),
    });
  }

  for (const voice of library ?? []) {
    if (takenProfiles.has(voice.id)) continue;
    if (voice.external_voice_id && ours.get(voice.external_voice_id) !== voice) continue;
    const meta = [languageLabel(voice.language), `персонаж «${voice.character_name}»`].join(' · ');
    const missingAtProvider = catalogLoaded && Boolean(voice.external_voice_id);
    const problem = !voice.usable
      ? describeVoiceStatus(voice)
      : missingAtProvider
        ? 'не работает, создайте заново'
        : null;
    options.push({
      key: `profile:${voice.id}`,
      voiceId: voice.external_voice_id,
      profileId: voice.id,
      name: voice.name,
      group: 'mine',
      meta,
      characterName: voice.character_name,
      previewUrl: null,
      usable: voice.usable && !missingAtProvider,
      problem,
      searchText: `${voice.name} ${voice.character_name} ${meta}`.toLowerCase(),
    });
  }

  options.sort((a, b) => {
    if (a.usable !== b.usable) return a.usable ? -1 : 1;
    return a.name.localeCompare(b.name, 'ru');
  });
  return options;
}

export function countByGroup(options: readonly VoiceOption[]): Record<VoiceGroup, number> {
  const counts: Record<VoiceGroup, number> = { mine: 0, pro: 0, std: 0 };
  for (const option of options) counts[option.group] += 1;
  return counts;
}

export function filterVoices(
  options: readonly VoiceOption[],
  group: VoiceGroup | 'all',
  query: string,
): VoiceOption[] {
  const needle = query.trim().toLowerCase();
  return options.filter((option) => {
    if (group !== 'all' && option.group !== group) return false;
    if (!needle) return true;
    return option.searchText.includes(needle);
  });
}
